import { useState, useEffect, useRef } from 'react';

// Protocol F: Battery Hygiene Constants
const THROTTLE_MS = 10000; // 10 seconds
const DISTANCE_THRESHOLD_M = 50; // 50 meters

interface LocationData {
    lat: number;
    lng: number;
    heading: number | null;
    speed: number | null;
    timestamp: number;
}

interface UseDriverLocationResult {
    location: LocationData | null;
    error: string | null;
    isBroadcasting: boolean;
}

// Haversine Formula for distance
const getDistanceFromLatLonInMeters = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371e3; // metres
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

export const useDriverLocation = (isTrackingActive: boolean): UseDriverLocationResult => {
    const [location, setLocation] = useState<LocationData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isBroadcasting, setIsBroadcasting] = useState(false);

    // Refs for throttling (non-rendering state)
    const lastEmittedTime = useRef<number>(0);
    const lastLocation = useRef<LocationData | null>(null);
    const watchId = useRef<number | null>(null);

    useEffect(() => {
        // Protocol G: Privacy Guard - Stop immediately if not tracking
        if (!isTrackingActive) {
            if (watchId.current !== null) {
                navigator.geolocation.clearWatch(watchId.current);
                watchId.current = null;
            }
            setIsBroadcasting(false);
            return;
        }

        if (!navigator.geolocation) {
            setError('Geolocation is not supported by this browser.');
            return;
        }

        setIsBroadcasting(true);

        const handlePosition = (position: GeolocationPosition) => {
            const { latitude, longitude, heading, speed } = position.coords;
            const now = Date.now();
            const newLocation: LocationData = {
                lat: latitude,
                lng: longitude,
                heading,
                speed,
                timestamp: now,
            };

            // Protocol F: Throttling Logic
            let shouldUpdate = false;

            // 1. First update always passes
            if (!lastLocation.current) {
                shouldUpdate = true;
            } else {
                // 2. Check Time
                const timeDelta = now - lastEmittedTime.current;

                // 3. Check Distance
                const distanceDelta = getDistanceFromLatLonInMeters(
                    lastLocation.current.lat,
                    lastLocation.current.lng,
                    newLocation.lat,
                    newLocation.lng
                );

                if (timeDelta >= THROTTLE_MS || distanceDelta >= DISTANCE_THRESHOLD_M) {
                    shouldUpdate = true;
                }
            }

            if (shouldUpdate) {
                lastEmittedTime.current = now;
                lastLocation.current = newLocation;
                setLocation(newLocation); // Trigger Re-render & Effect in consumer
            }
        };

        const handleError = (err: GeolocationPositionError) => {
            setError(err.message);
        };

        // ADR-010: High Accuracy enabled for moving vehicle tracking
        watchId.current = navigator.geolocation.watchPosition(handlePosition, handleError, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
        });

        // Cleanup function
        return () => {
            if (watchId.current !== null) {
                navigator.geolocation.clearWatch(watchId.current);
            }
        };
    }, [isTrackingActive]);

    return { location, error, isBroadcasting };
};
